/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */

import React from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { Box, Flex, Stack } from '@atlaskit/primitives/compiled';
import { Divider } from '@atlaskit/side-nav-items/menu-section';
import { token } from '@atlaskit/tokens';

import { useKeyboardReorderContext } from '../../../controllers/keyboard-reorder-provider';

import ReorderToggle from './reorder-toggle';
import { RibbonSkeleton } from './ribbon-skeleton';
import type { RibbonLayoutProps } from './types';

const styles = cssMap({
	container: {
		height: '100%',
		backgroundColor: token('color.background.accent.gray.subtlest'),
		paddingTop: token('space.050'),
	},
	contentContainer: {
		overflowY: 'auto',
		// this padding is added to ensure the focus ring on the top most app tile is not cut off when in selected state
		paddingTop: token('space.050'),
	},
	divider: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		width: '32px',
		'@media (min-width: 48rem)': {
			width: '40px',
		},
	},
});

export default function RibbonLayout({
	header,
	content,
	footer,
	hasLoaded = true,
	testId,
}: RibbonLayoutProps): JSX.Element {
	const { containerRef } = useKeyboardReorderContext();

	return (
		<Flex
			ref={containerRef}
			testId={testId}
			xcss={styles.container}
			direction="column"
			alignItems="center"
			data-layout-slot
		>
			{!hasLoaded ? (
				<RibbonSkeleton />
			) : (
				<>
					{header && (
						<>
							{header}
							<Box xcss={styles.divider}>
								<Divider />
							</Box>
						</>
					)}

					{content && (
						<Stack space="space.100" grow="fill" xcss={styles.contentContainer}>
							<Flex
								gap="space.150"
								direction="column"
								alignItems="center"
								testId={`${testId}--reorder-content`}
							>
								{content}
							</Flex>
						</Stack>
					)}

					<ReorderToggle />

					{footer && (
						<>
							<Box xcss={styles.divider}>
								<Divider />
							</Box>
							{footer}
						</>
					)}
				</>
			)}
		</Flex>
	);
}
